import { yupResolver } from '@hookform/resolvers/yup'
import { Box } from '@mui/material'
import CustomButton from 'components/buttons/CustomButton/CustomButton'
import CustomDialog from 'components/dialogs/CustomDialog'
import { useProfileContext } from 'context/ProfileContext'
import { useGetExperienceById, useUpdateReimbursement } from 'hooks/useUsers'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { snackbarService } from 'services/snackbarService'
import * as yup from 'yup';
import dayjs from 'lib/dayjs';
import { useAuth } from 'context/AuthContext'
import CustomFormSelect from 'components/inputs/CustomFormSelect/CustomFormSelect'
import { useGetReimbursementModels, useGetSpecialities, useGetStates, useGetThresholds } from 'hooks/useLookups'
import CustomFormTextInput from 'components/inputs/CustomFormTextInput/CustomFormTextInput'
import { showAmount, showRVUMultiplier, showThreshold } from 'common/caseHelpers'
import { EReimbursementModel } from 'enum/EReimbursementModel'
import { EThreshold } from 'enum/EThreshold'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useEffect } from 'react'

interface ReimbursementFormValues {
  practice_state: string;
  reimbursement_model: EReimbursementModel;
  threshold?: EThreshold | '';
  amount?: number;
  rvus_multiplier?: number;
}

interface Props {
  open: boolean,
  setOpen: (value: boolean) => void;
}

const validationSchema: yup.ObjectSchema<ReimbursementFormValues> = yup.object({
  practice_state: yup
    .string()
    .required('Practice state is required'),

  reimbursement_model: yup
    .mixed<EReimbursementModel>()
    .required('Reimbursement model is required')
    .notOneOf(
      [EReimbursementModel.EMPTY],
      'Reimbursement model is required'
    ),

  threshold: yup
    .mixed<EThreshold | ''>()
    .when('reimbursement_model', (model: any, schema) => {
      return showThreshold(model)
        ? schema.required('Threshold is required')
        : schema.optional().nullable();
    })
    .notOneOf(
      [EThreshold.EMPTY],
      'Threshold is required'
    ),

  amount: yup
    .number()
    .typeError('Amount must be a number')
    .when('reimbursement_model', (model: any, schema) => {
      return showAmount(model)
        ? schema.required('Amount is required')
        : schema.optional().nullable();
    }),

  rvus_multiplier: yup
    .number()
    .typeError('RVUs multiplier must be a number')
    .when('reimbursement_model', (model: any, schema) => {
      return showRVUMultiplier(model)
        ? schema.required('RVUs multiplier is required')
        : schema.optional().nullable();
    }),
});



export default function ReimbursementDialog({
  open,
  setOpen,
}: Props) {
  const { user } = useAuth();
  const { reimbursement, refetch } = useProfileContext();
  const { data: specialities } = useGetSpecialities();

  const { data: states } = useGetStates();
  const { data: reimbursementModels } = useGetReimbursementModels();
  const { data: thresholds } = useGetThresholds();

  const methods = useForm<ReimbursementFormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    criteriaMode: 'all',
    shouldUnregister: true,
    defaultValues: {
      practice_state: '',
      reimbursement_model: EReimbursementModel.EMPTY,
      threshold: '',
      amount: 0,
      rvus_multiplier: 0,
    }
  });

  const {
    handleSubmit,
    register,
    control,
    formState: { isValid },
    reset,
    setValue,
  } = methods;

  const reimbursementModel = useWatch({
    control,
    name: `reimbursement_model`,
  });

  const { mutate: updateMutation, isLoading: isUpdating } = useUpdateReimbursement(
    (data: any) => {
      if (data?.status_code == 200) {
        setOpen(false);
        snackbarService.showSnackbar(`Reimbursement info has been updated successfully`, 'success');
        refetch();
      } else {
        snackbarService.showSnackbar(data?.data ? data.data : 'Something went wrong', 'error');
      }
    },
    (err: any) => {
      console.log('err', JSON.stringify(err));
    }
  );

  const onSubmit = (data: any) => {
    updateMutation({
      userId: user.id,
      dto: data,
    });
  };

  useEffect(() => {
    if (!reimbursement) return;

    setTimeout(() => {
      reset({
        practice_state: reimbursement.practice_state ?? '',
        reimbursement_model:
          reimbursement.reimbursement_model ?? EReimbursementModel.EMPTY,
        threshold: reimbursement.threshold ?? '',
        amount: reimbursement.amount ?? 0,
        rvus_multiplier: reimbursement.rvus_multiplier ?? 0,
      });
    }, 500);
  }, [reimbursement, reset, open]);


  return (
    <CustomDialog
      open={open}
      title={`Add Reimbursement`}
      alignText='center'
      onClose={() => setOpen(false)}
      content={
        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
          >

            <Box mt={2}>
              <CustomFormSelect
                name={'practice_state'}
                options={states || []}
                floatingLabel='Practice state'
                placeholder='Practice state'
                required
              />
            </Box>

            <Box mt={2}>
              <CustomFormSelect
                name={'reimbursement_model'}
                options={reimbursementModels || []}
                floatingLabel='Reimbursement model'
                placeholder='Reimbursement model'
                required
              />
            </Box>

            {showThreshold(reimbursementModel) && <Box mt={2}>
              <CustomFormSelect
                name={'threshold'}
                options={thresholds || []}
                floatingLabel='Threshold'
                placeholder='Threshold'
                required
              />
            </Box>}

            {showAmount(reimbursementModel) && <Box mt={2}>
              <CustomFormTextInput
                name={'amount'}
                register={register}
                floatingLabel='Amount'
                placeholder='Amount'
                required
                type='number'
              />
            </Box>}

            {showRVUMultiplier(reimbursementModel) && <Box mt={2}>
              <CustomFormTextInput
                name={'rvus_multiplier'}
                register={register}
                floatingLabel='RVUs Multipler (Reimbursement per RVU)'
                placeholder='RVUs multipler'
                required
                type='number'
                endIcon={<AttachMoneyIcon sx={{ fontSize: 20 }} />}
              />
            </Box>}

            <Box my={2}>
              <CustomButton
                text={`Save`}
                color='primary'
                fullWidth
                type='submit'
                size='large'
                loading={isUpdating}
                disabled={!isValid}
              />
            </Box>

          </Box>
        </FormProvider>
      }
    />
  )
}
